import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import AuthModal from './AuthModal';
import './UserMenu.css';

const UserMenu: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Debug logging for UserMenu
  console.log('🎭 UserMenu render:', { 
    isAuthenticated, 
    user: user ? { name: user.name, email: user.email } : null 
  });

  // Calculate dropdown position when opening
  useEffect(() => {
    if (showUserDropdown && buttonRef.current) {
      const updatePosition = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + 4, // 4px gap below button
            right: window.innerWidth - rect.right // Position from right edge
          });
        }
      };

      updatePosition();
      
      // Update position on window resize
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showUserDropdown]);

  const handleLoginClick = () => {
    setShowAuthModal(true);
  };

  const handleLogout = () => {
    logout();
    setShowUserDropdown(false);
  };

  const handleUserAvatarClick = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  // Dropdown component that will be portaled
  const DropdownPortal = () => {
    if (!showUserDropdown) return null;

    return ReactDOM.createPortal(
      <>
        <div className="user-dropdown" style={{
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          right: `${dropdownPosition.right}px`,
          zIndex: 10000
        }}>
          <div className="user-info">
            <div className="user-email">{user?.email}</div>
            <div className="user-since">
              Member since {new Date(user?.createdAt || '').toLocaleDateString()}
            </div>
          </div>
          <div className="dropdown-divider"></div>
          <button className="dropdown-item logout-btn" onClick={handleLogout}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M6 2a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1H6zM5 3a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V3z"/>
              <path d="M11.5 8a.5.5 0 0 1-.5.5H8a.5.5 0 0 1 0-1h3a.5.5 0 0 1 .5.5z"/>
              <path d="M10.146 7.146a.5.5 0 0 1 .708.708L9.707 9l1.147 1.146a.5.5 0 0 1-.708.708L9 9.707l-1.146 1.147a.5.5 0 0 1-.708-.708L8.293 9 7.146 7.854a.5.5 0 1 1 .708-.708L9 8.293l1.146-1.147z"/>
            </svg>
            Sign Out
          </button>
        </div>
        <div 
          className="dropdown-backdrop" 
          onClick={() => setShowUserDropdown(false)}
        />
      </>,
      document.body
    );
  };

  if (!isAuthenticated) {
    return (
      <>
        <button className="login-btn" onClick={handleLoginClick}>
          Sign In
        </button>
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
        />
      </>
    );
  }

  return (
    <>
      <div className="user-menu">
        <button 
          ref={buttonRef}
          className="user-avatar"
          onClick={handleUserAvatarClick}
        >
          <div className="avatar-circle">
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <span className="user-name">{user?.name}</span>
          <svg 
            className={`dropdown-arrow ${showUserDropdown ? 'open' : ''}`}
            width="12" 
            height="12" 
            viewBox="0 0 12 12"
          >
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none"/>
          </svg>
        </button>
      </div>
      <DropdownPortal />
    </>
  );
};

export default UserMenu; 